// Client identity for the scope-aware memory system.
//
// When authenticated, getUserId() returns the real user ID from the auth
// layer. Otherwise it falls back to an anonymous UUID stamped on first load.
// The anonymous id survives page reloads but not localStorage clears.

import { getAuthUser } from "./auth";
import { STORAGE_AUTH } from "@/constants/storage";

const USER_ID_KEY = STORAGE_AUTH.userId;

function generate(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback for ancient browsers / test environments — not cryptographic,
  // just stable enough to key memory on.
  return "u_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function getUserId(): string {
  if (typeof window === "undefined") return "";
  // Authenticated users use their real server-issued ID.
  const authUser = getAuthUser();
  if (authUser?.id) return authUser.id;
  // Anonymous fallback: stable UUID per browser.
  try {
    const existing = window.localStorage.getItem(USER_ID_KEY);
    if (existing && existing.length > 0) return existing;
    const fresh = generate();
    window.localStorage.setItem(USER_ID_KEY, fresh);
    return fresh;
  } catch {
    return "";
  }
}

// clearUserId wipes the stored id. Exposed for test / debug tooling
// that wants to simulate a fresh user without clearing the rest of
// localStorage. Not wired to any UI yet.
export function clearUserId(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(USER_ID_KEY);
  } catch {
    // ignore — see getUserId
  }
}
